import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/axios';
import Header from '../components/Header';
import ProgressBar from '../components/ProgressBar';

const OrcamentoAnaliseNew = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messageIndex, setMessageIndex] = useState(0);
  const [filePreview, setFilePreview] = useState(null);

  const messages = [
    'Analisando planta...',
    'Identificando comodos...',
    'Calculando areas...',
    'Detectando itens especiais...',
    'Preparando orcamento...',
  ];

  // Rotate messages every 3 seconds
  useEffect(() => {
    const messageInterval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % messages.length);
    }, 3000);

    return () => clearInterval(messageInterval);
  }, [messages.length]);

  // Main effect: fetch orcamento and start analysis
  useEffect(() => {
    const analyzeBlueprint = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Step 1: Fetch orcamento data
        const orcamentoResponse = await api.get(
          `/api/orcamento/${id}`
        );
        const orcamentoData = orcamentoResponse.data;

        // Extract file info and config from orcamento
        if (!orcamentoData.arquivo) {
          throw new Error('Arquivo de planta não encontrado');
        }

        const { arquivo, tipo, padrao } = orcamentoData;

        // Set file preview if available
        if (arquivo) {
          setFilePreview(arquivo.nome || 'Planta carregada');
        }

        // Step 2: Call analysis endpoint
        const analysisResponse = await api.post(
          '/api/orcamento/analisar',
          {
            file: arquivo.dados, // base64 encoded file data
            mimeType: arquivo.mimeType,
            tipo: tipo || 'comercial',
            padrao: padrao || 'padrao',
          }
        );

        const analysisResult = analysisResponse.data;

        // Step 3: Store analysis result in localStorage
        localStorage.setItem(
          `orcamento_analise_${id}`,
          JSON.stringify(analysisResult)
        );

        // Step 4: Redirect to validation page
        setIsLoading(false);
        setTimeout(() => {
          navigate(`/orcamento/${id}/validacao`);
        }, 1000);
      } catch (err) {
        console.error('Erro ao analisar planta:', err);
        setError(
          err.response?.data?.message ||
            err.message ||
            'Erro ao analisar a planta. Tente novamente.'
        );
        setIsLoading(false);
      }
    };

    analyzeBlueprint();
  }, [id, navigate]);

  const handleRetry = () => {
    setError(null);
    setMessageIndex(0);
    window.location.reload();
  };

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)] px-4">
          <div className="max-w-lg w-full bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="mb-6">
              <svg
                className="w-16 h-16 mx-auto text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Ops! Algo deu errado
            </h2>
            <p className="text-gray-600 mb-8">{error}</p>
            <button
              onClick={handleRetry}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
            >
              Tentar Novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="flex items-center justify-center min-h-[calc(100vh-80px)] px-4">
        <div className="max-w-lg w-full">
          {/* Bruno Image */}
          <style>{`
            @keyframes float {
              0%, 100% {
                transform: translateY(0px);
              }
              50% {
                transform: translateY(-12px);
              }
            }
            .bruno-float {
              animation: float 3s ease-in-out infinite;
            }
          `}</style>

          <div className="flex justify-center mb-8">
            <img
              src="/images/brand/bruno-lupa.png"
              alt="Bruno analisando"
              className="w-32 h-32 object-contain bruno-float"
            />
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold text-center text-gray-800 mb-8">
            Analisando sua planta...
          </h1>

          {/* Progress Bar */}
          <div className="mb-8">
            <ProgressBar animated={true} />
          </div>

          {/* Rotating Messages */}
          <div className="mb-8 h-12 flex items-center justify-center">
            <p className="text-lg font-semibold text-blue-600 text-center transition-opacity duration-500">
              {messages[messageIndex]}
            </p>
          </div>

          {/* File Preview */}
          {filePreview && (
            <div className="bg-white rounded-lg p-4 text-center border border-gray-200">
              <p className="text-sm text-gray-600">
                <span className="font-semibold">Arquivo:</span> {filePreview}
              </p>
            </div>
          )}

          {/* Additional context message */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              Este processo pode levar alguns segundos...
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrcamentoAnaliseNew;
